/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { X, CheckCircle2, Info, AlertTriangle } from 'lucide-react'
import { cn } from '../../lib/utils'

export type ToastVariant = 'default' | 'success' | 'info' | 'warning' | 'error'

export interface ToastOptions {
  id?: string
  title?: string
  description?: string
  variant?: ToastVariant
  // Visible active (tab focused) milliseconds. Defaults to 6000ms.
  durationMs?: number
}

interface InternalToast extends Required<Omit<ToastOptions, 'id'>> { id: string; activeMs: number }

interface ToastContextValue {
  addToast: (opts: ToastOptions) => string
  removeToast: (id: string) => void
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<InternalToast[]>([])

  const removeToast = React.useCallback((id: string) => {
    setToasts(t => t.filter(toast => toast.id !== id))
  }, [])

  const addToast = React.useCallback((opts: ToastOptions) => {
    const id = opts.id || crypto.randomUUID()
    setToasts(t => [
      ...t,
      {
        id,
        title: opts.title || '',
        description: opts.description || '',
        variant: opts.variant || 'default',
        durationMs: typeof opts.durationMs === 'number' ? opts.durationMs : 6000,
        activeMs: 0,
      }
    ])
    return id
  }, [])

  // Active usage timing: only decrement remaining while tab is visible
  React.useEffect(() => {
    if (toasts.length === 0) return
    let last = performance.now()
    const interval = setInterval(() => {
      const now = performance.now()
      const delta = now - last
      last = now
      if (document.visibilityState !== 'visible') return
      setToasts(current => current.filter(t => {
        const nextActive = t.activeMs + delta
        if (nextActive >= t.durationMs) return false
        t.activeMs = nextActive
        return true
      }))
    }, 250)
    return () => clearInterval(interval)
  }, [toasts.length])

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// Presentation components

const variantIconClasses: Record<ToastVariant, string> = {
  default: 'text-foreground/70',
  success: 'text-success-600',
  info: 'text-info-600',
  warning: 'text-warning-600',
  error: 'text-danger-600',
}

const VariantIcon: React.FC<{ variant: ToastVariant }> = ({ variant }) => {
  switch (variant) {
    case 'success': return <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
    case 'info': return <Info className="h-5 w-5" aria-hidden="true" />
    case 'warning': return <AlertTriangle className="h-5 w-5" aria-hidden="true" />
    case 'error': return <AlertTriangle className="h-5 w-5" aria-hidden="true" />
    default: return <Info className="h-5 w-5" aria-hidden="true" />
  }
}

const ToastViewport: React.FC<{ toasts: InternalToast[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed left-0 bottom-0 z-[70] flex flex-col items-start space-y-2 px-4 pb-4 pointer-events-none"
    >
      {toasts.map(t => (
        <div
          key={t.id}
          role="status"
            className={cn(
            'pointer-events-auto w-full max-w-sm rounded-xl border border-black/10 shadow-lg backdrop-blur bg-white/90 px-4 py-3 flex gap-3 items-start relative animate-in fade-in slide-in-from-bottom-2',
            'will-change-transform'
          )}
        >
          <div className={cn('mt-0.5', variantIconClasses[t.variant])}>
            <VariantIcon variant={t.variant} />
          </div>
          <div className="flex-1 min-w-0">
            {t.title && <p className="text-sm font-medium text-foreground truncate" title={t.title}>{t.title}</p>}
            {t.description && <p className="mt-0.5 text-xs text-foreground/70 whitespace-pre-wrap" title={t.description}>{t.description}</p>}
          </div>
          <button
            onClick={() => onDismiss(t.id)}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-black/5 transition-colors cursor-pointer"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
          {/* Progress bar (active visible time) */}
          <div className="absolute left-0 bottom-0 h-0.5 w-full bg-black/5 overflow-hidden rounded-b-xl">
            <div
              className={cn('h-full transition-[width] duration-300 ease-linear', {
                'bg-success-600': t.variant === 'success',
                'bg-info-600': t.variant === 'info' || t.variant === 'default',
                'bg-warning-600': t.variant === 'warning',
                'bg-danger-600': t.variant === 'error',
              })}
              style={{ width: `${(t.activeMs / t.durationMs) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ToastProvider
